(function () {
  const SELECTOR = '[data-rotating-claims-pill]';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  if (window.__rcpManager) {
    window.__rcpManager.initAll(document);
    return;
  }

  class RotatingClaimsPill {
    constructor(root) {
      this.root = root;
      this.claims = [];
      this.currentIndex = 0;
      this.timerId = null;
      this.animationId = null;
      this.isPaused = false;
      this.isDestroyed = false;

      this.intervalMs = this.parseMs(root.dataset.intervalMs, 3500, 2000, 8000);
      this.animationMs = this.parseMs(root.dataset.animationMs, 450, 0, 5000);

      this.motionQuery = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
      this.reducedMotion = Boolean(this.motionQuery && this.motionQuery.matches);

      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMotionChange = this.onMotionChange.bind(this);
    }

    init() {
      this.claims = this.collectClaims();
      if (this.claims.length === 0) {
        this.root.hidden = true;
        return false;
      }
      this.root.hidden = false;

      this.pillHost = this.root.querySelector('[data-rcp-pill-host]');
      this.pillEl = this.root.querySelector('[data-rcp-pill]');
      if (!this.pillHost || !this.pillEl) return false;

      this.root.classList.toggle('rcp--reduced-motion', this.reducedMotion);
      this.root.style.setProperty('--rcp-animation-ms', `${this.animationMs}ms`);

      this.renderClaim(0, false);

      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.motionQuery) {
        if (typeof this.motionQuery.addEventListener === 'function') {
          this.motionQuery.addEventListener('change', this.onMotionChange);
        } else {
          this.motionQuery.addListener(this.onMotionChange);
        }
      }

      if (!this.reducedMotion && this.claims.length > 1) {
        this.startRotation();
      }

      return true;
    }

    destroy() {
      this.isDestroyed = true;
      this.stopRotation();

      if (this.animationId) {
        window.clearTimeout(this.animationId);
        this.animationId = null;
      }

      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.motionQuery) {
        if (typeof this.motionQuery.removeEventListener === 'function') {
          this.motionQuery.removeEventListener('change', this.onMotionChange);
        } else {
          this.motionQuery.removeListener(this.onMotionChange);
        }
      }
    }

    collectClaims() {
      const claimNodes = this.root.querySelectorAll('[data-rcp-claim]');
      return Array.from(claimNodes)
        .map((node) => {
          const plain = `${node.getAttribute('data-rcp-plain') || ''}`.trim();
          const html = `${node.innerHTML || ''}`.trim();
          const url = `${node.getAttribute('data-rcp-url') || ''}`.trim();

          return { plain, html, url };
        })
        .filter((claim) => claim.plain !== '');
    }

    renderClaim(index, animate) {
      const claim = this.claims[index];
      if (!claim) return;

      this.currentIndex = index;
      this.ensurePillTag(Boolean(claim.url));
      this.syncPillRefs();
      if (!this.currentLineEl || !this.nextLineEl || !this.srTextEl || !this.pillEl) return;

      this.updateLinkState(claim);
      this.updateAccessibleText(claim.plain);

      if (!animate || this.reducedMotion) {
        this.pillEl.classList.remove('is-animating', 'is-leaving', 'is-entering');
        if (this.animationId) {
          window.clearTimeout(this.animationId);
          this.animationId = null;
        }
        this.currentLineEl.innerHTML = claim.html;
        this.nextLineEl.innerHTML = '';
        return;
      }

      if (this.animationId) {
        window.clearTimeout(this.animationId);
      }

      this.nextLineEl.innerHTML = claim.html;
      this.pillEl.classList.remove('is-leaving', 'is-entering');

      void this.pillEl.offsetHeight;
      this.pillEl.classList.add('is-animating', 'is-leaving', 'is-entering');

      this.animationId = window.setTimeout(() => {
        if (this.isDestroyed) return;
        this.currentLineEl.innerHTML = claim.html;
        this.nextLineEl.innerHTML = '';
        this.pillEl.classList.remove('is-animating', 'is-leaving', 'is-entering');
        this.animationId = null;
      }, this.animationMs);
    }

    ensurePillTag(hasLink) {
      if (!this.pillEl || !this.pillHost) return;

      const desiredTag = hasLink ? 'A' : 'DIV';
      if (this.pillEl.tagName === desiredTag) return;

      const hadFocus = document.activeElement === this.pillEl;
      const replacement = document.createElement(desiredTag.toLowerCase());
      replacement.className = this.pillEl.className;
      replacement.setAttribute('data-rcp-pill', '');

      while (this.pillEl.firstChild) {
        replacement.appendChild(this.pillEl.firstChild);
      }

      this.pillEl.replaceWith(replacement);
      this.pillEl = replacement;

      if (hadFocus && typeof replacement.focus === 'function') {
        replacement.focus();
      }
    }

    updateLinkState(claim) {
      if (!this.pillEl) return;

      if (this.pillEl.tagName === 'A' && claim.url) {
        this.pillEl.setAttribute('href', claim.url);
      } else {
        this.pillEl.removeAttribute('href');
      }

      this.pillEl.setAttribute('aria-label', claim.plain);
    }

    updateAccessibleText(plainText) {
      if (this.srTextEl) {
        this.srTextEl.textContent = plainText;
      }
    }

    syncPillRefs() {
      if (!this.pillEl) return;

      this.currentLineEl = this.pillEl.querySelector('[data-rcp-current]');
      this.nextLineEl = this.pillEl.querySelector('[data-rcp-next]');
      this.srTextEl = this.pillEl.querySelector('[data-rcp-sr]');
    }

    startRotation() {
      if (this.timerId || this.reducedMotion || this.claims.length <= 1 || this.isDestroyed) return;

      this.timerId = window.setInterval(() => {
        if (this.isPaused || document.hidden) return;
        const nextIndex = (this.currentIndex + 1) % this.claims.length;
        this.renderClaim(nextIndex, true);
      }, this.intervalMs);
    }

    stopRotation() {
      if (!this.timerId) return;
      window.clearInterval(this.timerId);
      this.timerId = null;
    }

    onMouseEnter() {
      this.isPaused = true;
      this.stopRotation();
    }

    onMouseLeave() {
      if (this.root.contains(document.activeElement)) return;

      this.isPaused = false;
      this.startRotation();
    }

    onFocusIn() {
      this.isPaused = true;
      this.stopRotation();
    }

    onFocusOut(event) {
      const relatedTarget = event.relatedTarget;
      if (relatedTarget instanceof Node && this.root.contains(relatedTarget)) return;
      if (this.root.matches(':hover')) return;

      this.isPaused = false;
      this.startRotation();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopRotation();
        return;
      }

      if (!this.isPaused) {
        this.startRotation();
      }
    }

    onMotionChange(event) {
      this.reducedMotion = Boolean(event.matches);
      this.root.classList.toggle('rcp--reduced-motion', this.reducedMotion);

      if (this.reducedMotion) {
        this.stopRotation();
        this.renderClaim(0, false);
        return;
      }

      this.renderClaim(this.currentIndex, false);
      if (!this.isPaused) {
        this.startRotation();
      }
    }

    parseMs(value, fallback, min, max) {
      const parsed = Number.parseInt(`${value || ''}`, 10);
      if (Number.isNaN(parsed)) return fallback;
      return Math.min(Math.max(parsed, min), max);
    }
  }

  const manager = {
    instances: new Map(),

    initAll(container) {
      const scope = container instanceof Element || container instanceof Document ? container : document;
      scope.querySelectorAll(SELECTOR).forEach((root) => {
        this.initRoot(root);
      });
    },

    initRoot(root) {
      if (!(root instanceof HTMLElement)) return;
      if (this.instances.has(root)) return;

      const instance = new RotatingClaimsPill(root);
      if (!instance.init()) return;
      this.instances.set(root, instance);
    },

    destroyIn(container) {
      const scope = container instanceof Element || container instanceof Document ? container : document;
      scope.querySelectorAll(SELECTOR).forEach((root) => {
        this.destroyRoot(root);
      });
    },

    destroyRoot(root) {
      if (!(root instanceof HTMLElement)) return;
      const instance = this.instances.get(root);
      if (!instance) return;

      instance.destroy();
      this.instances.delete(root);
    },
  };

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  onReady(() => {
    manager.initAll(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      manager.initAll(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) {
      manager.destroyIn(event.target);
    }
  });

  window.__rcpManager = manager;
})();
